import { Component, OnInit } from '@angular/core';
import { SearchResourceType } from '@app/core/model/search/search-resource-type';
import { ISearchResultItem } from '@app/core/model/search/search-result-item';
import { ISearchResourceConfig } from '@app/core/services/resource-dictionary/search-resource-dictionary-types';
import { SearchResourceDictionaryService } from '@app/core/services/resource-dictionary/search-resource-dictionary.service';
import { SearchDatasourceFactoryService } from '@app/search/datasource/search-datasource-factory.service';
import { GridOptions, IChooserServerSideDataParams, NgxSmartModalService } from '@bssh/comp-lib';
import _ from 'lodash';
import { BaseModalComponent, ModalComponentType } from '../base-modal/base-modal.component';
import { ModalTexts } from '../modal-texts';
import { IChooserModalInput, IChooserModalOutput } from '../model/action-modal';
import { ChooserGridOptionsFactoryService } from './gridOptions/chooser-grid-options-factory.service';
import { LaunchFormChooserGridOptionsFactoryService } from './gridOptions/launch-form-chooser-grid-options-factory.service';

@Component({
  selector: 'app-rad-chooser-modal',
  templateUrl: './rad-chooser-modal.component.html',
  styleUrls: ['./rad-chooser-modal.component.scss']
})
export class RadChooserModalComponent extends BaseModalComponent implements OnInit {
  modalComponentType = ModalComponentType.RAD;
  closable: boolean = true;
  modalType = 'radResourceChooserModal';
  public chooserTitle = '';
  public chooserTitleTooltip = '';
  public chooserSearchPlaceHolderText = '';
  public hasMatcher = false;
  public debounceTimeInMs = 300;
  public rowSelection: 'single' | 'multiple' = 'multiple';
  public isResourceCreatable = false;
  public confirmButtonText = ModalTexts.CHOOSER.CONFIRM_BUTTON_TEXT;
  public newButtonText: string;
  public chooserServerSideParams: IChooserServerSideDataParams;
  public gridOptions: GridOptions;
  public resourceType: SearchResourceType;
  public initiallySelectedItems: ISearchResultItem[] = [];
  public newlyCreatedResourceId: string;
  public resourceDictionary: ISearchResourceConfig;
  public trackLibraryPreps = false;
  public titleInfoPopoverContent: string;
  public tableId: string;

  constructor(
    public ngxSmartModalService: NgxSmartModalService,
    private searchDatasourceFactory: SearchDatasourceFactoryService,
    private chooserGridOptionsFactory: ChooserGridOptionsFactoryService,
    private launchFormChooserGridOptionsFactory: LaunchFormChooserGridOptionsFactoryService,
    private searchResourceDictionaryService: SearchResourceDictionaryService
) {
    super(ngxSmartModalService);
   }

  ngOnInit() {
    super.ngOnInit();

    this.subs.sink = this.data.subscribe((data: IChooserModalInput) => {
          if (data) {
            this.chooserTitle = data.title;
            this.chooserTitleTooltip = data.titleTooltip;
            this.chooserSearchPlaceHolderText = data.searchPlaceHolderText;
            this.rowSelection = data.rowSelection;
            this.hasMatcher = !!data.hasMatcher;
            this.debounceTimeInMs = _.get(data, 'debounceTimeForRawTextQuery', this.debounceTimeInMs);
            this.isResourceCreatable = !!data.isResourceCreatable;
            this.resourceType = data.resourceType as SearchResourceType;
            this.trackLibraryPreps = data.trackLibraryPreps || false;
            this.titleInfoPopoverContent = data.titleInfoPopoverContent;
            this.tableId = data.tableId;

            this.resourceDictionary = this.searchResourceDictionaryService.getResourceConfigByType(this.resourceType);

            this.newButtonText = ModalTexts.CHOOSER.NEW_BUTTON_TEXT(this.resourceType as string);
            this.initiallySelectedItems = data.initallySelectedItems || [];

            this.newlyCreatedResourceId = data.newlyCreatedResourceId;

            const searchDataSource = this.searchDatasourceFactory.getSearchDatasourceForResource(
              this.resourceType, 
              {
                ...data.searchDataSourceOptions,
              }
            );
            this.chooserServerSideParams = {
              serverSideRequestOptions: searchDataSource.requestOptions,
              rawTextFilterProcessing: searchDataSource.rawTextFilterProcessing,
            };
            
            // Select the appropriate factory based on chooserContext
            let factory;
            switch(data.chooserContext) {
              case 'app-launch':
                factory = this.launchFormChooserGridOptionsFactory;
                break;
              default:
                factory = this.chooserGridOptionsFactory;
                break;
            }
            
            const searchGridOptions = factory.getGridOptionsFormatterForResource(this.resourceType);
            searchGridOptions.rowSelectionParams = {
              currentUserId: data.currentUserId,
              requiredPermissions: data.requiredPermissions,
              supportedGenomeBuilds: data.supportedGenomeBuilds
            };

            // Need to manually alter first header in table so that "select all" checkbox appears
            const columnDefinitions = searchGridOptions.getColumnDefinitions();
            if(this.rowSelection === 'multiple') {
              columnDefinitions[0].headerClass = 'select-all-current-page';
            }

            // Set up context for cell renderers
            const gridContext: any = { componentParent: this };
            
            // Add library prep selection callback if needed
            if (this.trackLibraryPreps && this.resourceType === SearchResourceType.Biosample) {
              gridContext.trackLibraryPreps = true;
              // The actual callback will be set in the resource chooser component
            }

            this.gridOptions = searchGridOptions.getGridOptions({
              columnDefs: columnDefinitions,
              rowSelection: this.rowSelection,
              suppressRowClickSelection: false,
              rowMultiSelectWithClick: this.rowSelection === 'multiple' ? true : false,
              context: gridContext,
              rowHeight: 40,
              domLayout: 'autoHeight',
              suppressScrollOnNewData: true,  // Prevents scroll position reset
              suppressHorizontalScroll: false,  // Explicitly enable horizontal scroll
              suppressColumnVirtualisation: true
            });
          }
        });
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  chooserItemsSelected(items: ISearchResultItem[]) {
    const data: IChooserModalOutput<ISearchResultItem> = {
      selectedItems: items
    };
    
    this.modal.setData(data, true);
  }

}
